/**
 * Canvas Type System
 *
 * Maps unknown/domain-specific node types to known renderers via a `canvas_type`
 * property on space objects. This enables AI agents to create rich, domain-specific
 * node types that render meaningfully using existing renderers.
 *
 * See specs/ai.md for the Transform Layer concept this implements.
 */

/** Known renderer types that canvas_type.render_as can target */
export const KNOWN_RENDER_TYPES = [
	'markdown',
	'image',
	'topic',
	'object',
	'header',
	'sticky-note',
] as const;

export type KnownRenderType = (typeof KNOWN_RENDER_TYPES)[number];

/**
 * Documents the fields each renderer expects.
 * Used by AI agents to construct correct field_map entries.
 */
export const RENDERER_FIELDS: Record<
	KnownRenderType,
	{ required: string[]; optional: string[] }
> = {
	markdown: {
		required: ['content'],
		optional: ['title'],
	},
	image: {
		required: ['src'],
		optional: ['title', 'caption', 'alt', 'objectFit', 'labelPosition', 'showLabels'],
	},
	topic: {
		required: [],
		optional: ['title', 'description', 'emoji'],
	},
	header: {
		required: ['content'],
		optional: [],
	},
	'sticky-note': {
		required: ['content'],
		optional: [],
	},
	object: {
		required: [],
		optional: [],
	},
};

/** Structure stored on space objects as `canvas_type` */
export interface CanvasType {
	render_as: string;
	field_map: Record<string, string>;
	description?: string;
}

const knownRenderSet = new Set<string>(KNOWN_RENDER_TYPES);

/**
 * Parse and validate a canvas_type value from an SDK object.
 * Returns null if the value is missing, malformed, or has an unknown render_as.
 */
export function parseCanvasType(value: unknown): CanvasType | null {
	if (value == null || typeof value !== 'object') return null;

	const obj = value as Record<string, unknown>;

	const renderAs = obj.render_as;
	if (typeof renderAs !== 'string' || !knownRenderSet.has(renderAs)) return null;

	const fieldMap = obj.field_map;
	if (fieldMap != null && typeof fieldMap !== 'object') return null;

	// Build a validated field_map — only keep string→string entries
	const validMap: Record<string, string> = {};
	if (fieldMap && typeof fieldMap === 'object') {
		for (const [key, val] of Object.entries(fieldMap as Record<string, unknown>)) {
			if (typeof val === 'string') {
				validMap[key] = val;
			}
		}
	}

	return {
		render_as: renderAs,
		field_map: validMap,
		...(typeof obj.description === 'string' && { description: obj.description }),
	};
}

/**
 * Apply field mappings to a cloned object.
 * Copies source field values to target fields. Does NOT overwrite existing target
 * fields, and does NOT delete source fields (preserves original data).
 *
 * Returns true if any mapping was applied.
 */
export function applyFieldMap(
	obj: Record<string, unknown>,
	fieldMap: Record<string, string>,
): boolean {
	let applied = false;
	for (const [sourceField, targetField] of Object.entries(fieldMap)) {
		if (sourceField in obj && !(targetField in obj)) {
			obj[targetField] = obj[sourceField];
			applied = true;
		}
	}
	return applied;
}
